'use client';

import { ComponentProps, ReactNode, useMemo } from 'react';
import { Tooltip } from 'recharts';

import { cn } from '../../../lib';

import { useChart } from './ChartContainer';
import { ChartTooltipIndicator } from './ChartTooltipIndicator';
import type { IChartConfig } from './types';
import { getPayloadConfigFromPayload } from './utils';

export const ChartTooltip = Tooltip;

type TTooltipItem = {
  color: string;
  dataKey: string;
  name: string;
  payload: {
    fill?: string;
    [key: string]: unknown;
  };
  value: number | string;
};

interface IChartTooltipContentProps extends ComponentProps<'div'> {
  active?: boolean;
  color?: string;
  formatter?: (
    value: TTooltipItem['value'],
    name: string,
    item: TTooltipItem,
    index: number,
    payload: TTooltipItem['payload']
  ) => ReactNode;
  hideIndicator?: boolean;
  hideLabel?: boolean;
  indicator?: 'line' | 'dot' | 'dashed';
  label?: ReactNode;
  labelClassName?: string;
  labelFormatter?: (value: ReactNode, payload?: TTooltipItem[]) => ReactNode;
  labelKey?: string;
  nameKey?: string;
  payload?: TTooltipItem[];
}

interface IRenderTooltipItemProps {
  formatter?: (
    value: TTooltipItem['value'],
    name: string,
    item: TTooltipItem,
    index: number,
    payload: TTooltipItem['payload']
  ) => ReactNode;
  hideIndicator?: boolean;
  index: number;
  indicator?: 'line' | 'dot' | 'dashed';
  indicatorColor?: string;
  item: TTooltipItem;
  itemConfig?: IChartConfig[string];
  nestLabel?: boolean;
  tooltipLabel?: ReactNode | null;
}

/**
 * -----------------------------------------------------------------------------
 * Renders a single tooltip item.
 */
function renderTooltipItem({
  formatter,
  hideIndicator,
  index,
  indicator,
  indicatorColor,
  item,
  itemConfig,
  nestLabel,
  tooltipLabel,
}: IRenderTooltipItemProps) {
  if (formatter && item?.value !== undefined && item.name) {
    return formatter(item.value, item.name, item, index, item.payload);
  }

  return (
    <>
      <ChartTooltipIndicator
        itemConfig={itemConfig}
        hideIndicator={hideIndicator}
        indicator={indicator}
        indicatorColor={indicatorColor}
        nestLabel={nestLabel}
      />
      <div
        className={cn(
          'flex flex-1 justify-between leading-none',
          nestLabel ? 'items-end' : 'items-center'
        )}
      >
        <div className="grid gap-1.5">
          {nestLabel ? tooltipLabel : null}
          <span className="text-muted-foreground">
            {itemConfig?.label || item.name}
          </span>
        </div>
        {item.value ? (
          <span className="text-foreground font-mono font-medium tabular-nums">
            {item.value.toLocaleString()}
          </span>
        ) : null}
      </div>
    </>
  );
}

/**
 * -----------------------------------------------------------------------------
 * Renders the tooltip content for the chart.
 */
export function ChartTooltipContent({
  active,
  className,
  color,
  formatter,
  hideIndicator = false,
  hideLabel = false,
  indicator = 'dot',
  label,
  labelClassName,
  labelFormatter,
  labelKey,
  nameKey,
  payload,
}: IChartTooltipContentProps) {
  const { config } = useChart();

  const tooltipLabel = useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null;
    }

    const [item] = payload;
    const key = `${labelKey || item?.dataKey || item?.name || 'value'}`;
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value =
      !labelKey && typeof label === 'string'
        ? config[label as keyof typeof config]?.label || label
        : itemConfig?.label;

    if (labelFormatter) {
      return (
        <div className={cn('font-medium', labelClassName)}>
          {labelFormatter(value, payload)}
        </div>
      );
    }

    if (!value) {
      return null;
    }

    return <div className={cn('font-medium', labelClassName)}>{value}</div>;
  }, [
    label,
    labelFormatter,
    payload,
    hideLabel,
    labelClassName,
    config,
    labelKey,
  ]);

  if (!active || !payload?.length) {
    return null;
  }

  const nestLabel = payload.length === 1 && indicator !== 'dot';

  return (
    <div
      className={cn(
        `border-border/50 bg-background grid min-w-[8rem] items-start gap-1.5
        rounded-lg border px-2.5 py-1.5 text-xs shadow-xl`,
        className
      )}
    >
      {!nestLabel ? tooltipLabel : null}
      <div className="grid gap-1.5">
        {payload.map((item, index) => {
          const key = `${nameKey || item.name || item.dataKey || 'value'}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          const indicatorColor = color || item.payload.fill || item.color;

          return (
            <div
              key={item.dataKey}
              className={cn(
                `[&>svg]:text-muted-foreground flex w-full flex-wrap
                items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5`,
                indicator === 'dot' && 'items-center'
              )}
            >
              {renderTooltipItem({
                formatter,
                item,
                index,
                itemConfig,
                hideIndicator,
                indicator,
                indicatorColor,
                nestLabel,
                tooltipLabel,
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

